import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import React from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import tw from "twrnc";

import { useLanguageStore } from "../features/quiz-core/languageStore";
import { useUserProfile } from "../features/quiz-core/useStore";
import { t } from "../i18n/translations";
import { iqesColors } from "../shared/ui/iqesDesignSystem";

import AnimatedHeaderText from "./AnimatedHeaderText";

interface HeaderBarProps {
  title: string;
  onBack?: () => void;
  onClose?: () => void;
  showProfile?: boolean;
  showClose?: boolean;
  variant?: "default" | "simple";
}

function getInitials(name?: string) {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const HeaderBar: React.FC<HeaderBarProps> = ({
  title,
  onBack,
  onClose,
  showProfile = true,
  showClose = true,
  variant = "default",
}) => {
  const navigation = useNavigation();
  const { name, avatar_url } = useUserProfile();
  const { language } = useLanguageStore();
  const initials = getInitials(name);
  const isSimple = variant === "simple";

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };
  return (
    <View
      style={[
        tw`flex-row items-center px-4 pt-8 pb-4`,
        {
          backgroundColor: isSimple ? iqesColors.canvas : 'rgba(8,10,24,0.88)',
          borderRadius: isSimple ? 0 : 20,
          borderWidth: 1,
          borderColor: isSimple ? 'rgba(246,190,58,0.28)' : 'rgba(246,190,58,0.32)',
        }
      ]}
      data-oid="o944z8f"
    >
      <TouchableOpacity
        onPress={handleBack}
        accessibilityLabel={t('coreUI.backButton', language)}
        style={tw`p-2`}
        data-oid="4g.w0ud"
      >
        <Ionicons name="chevron-back" size={28} color={iqesColors.goldBright} data-oid="huz5sro" />
      </TouchableOpacity>
      {showProfile ? (
        <View style={tw`flex-row items-center mr-2`} accessibilityLabel={`${t('coreUI.user', language)}: ${name || t('coreUI.player', language)}`}>
          <View
            style={[
              tw`bg-yellow-400 rounded-full items-center justify-center mr-2`,
              { width: 36, height: 36 }
            ]}
            accessibilityLabel={`${t('coreUI.userAvatar', language)} ${name || t('coreUI.player', language)}`}
          >
            {avatar_url ? (
              <Image
                source={{ uri: avatar_url }}
                accessibilityLabel={`${t('coreUI.userAvatar', language)} ${name || t('coreUI.player', language)}`}
                style={{ width: 36, height: 36, borderRadius: 18 }}
              />
            ) : (
              <Text style={tw`text-base font-bold text-white`}>
                {initials}
              </Text>
            )}
          </View>
          <Text style={[tw`text-base`, { color: iqesColors.ivory }]}>
            {name || t('coreUI.player', language)}
          </Text>
        </View>
      ) : (
        <View style={tw`w-10`} />
      )}
      <AnimatedHeaderText
        style={[
          tw`text-center flex-1`,
          {
            color: isSimple ? iqesColors.ivory : iqesColors.goldBright,
            fontWeight: isSimple ? '700' : 'normal',
            fontSize: isSimple ? 22 : 24
          }
        ]}
        numberOfLines={1}
        data-oid="y95lgni"
      >
        {title}
      </AnimatedHeaderText>
      {showClose ? (
        <TouchableOpacity
          onPress={handleClose}
          accessibilityLabel={t('coreUI.closeButton', language)}
          style={tw`p-2`}
          data-oid="8skft8q"
        >
          <Ionicons name="close" size={24} color={iqesColors.goldBright} data-oid="8.24j4u" />
        </TouchableOpacity>
      ) : (
        <View style={tw`w-10`} />
      )}
    </View>
  );
};

export default HeaderBar;
